import moment from "moment/moment";
import React, { type FC, useState } from "react";
import { useApiCall } from "../api/api";
import type { Company, Exam, ExamParticipant, Project } from "../api/dto";
import { Input } from "../components/form/Input";
import { Toggle } from "../components/form/Toggle";
import { ModalFooter } from "../components/layout/ModalProvider";
import { useRefresh } from "../components/RefreshController";
import { getDashboardDataFileName } from "../util/getOverallDataFileName";

interface ExportManagementDashboardModalProps {
  allCompanies: Company[];
  allProjects: Project[];
  allExams: Exam[];
  allParticipants: ExamParticipant[];
  company: Company;
  onlyExams: Exam["id"][];
  onlyContractors: ExamParticipant["contractor"][] | null;
  allContractors: ExamParticipant["contractor"][];
  onlySubContractors: ExamParticipant["subContractor"][] | null;
  onlyParticipants: ExamParticipant["id"][];
}
export const ExportManagementDashboardModal: FC<ExportManagementDashboardModalProps> = (props) => {
  const { exportSelectionAdminDashboardPdf, exportSelectionAdminDashboardPdfPerContractor } =
    useApiCall();
  const [groupedContractors, setGroupedContractors] = useState<string[]>([]);
  const exams = props.allExams.filter((exam) => props.onlyExams.includes(exam.id));
  const examDays = Array.from(new Set(exams.map((e) => moment(e.examAt).format("YYYY-MM-DD"))));
  const [exportPerContractor, setExportPerContractor] = useState(false);
  const exportAndDownload = async () => {
    const contractorsFilter =
      props.onlyContractors !== null && props.onlyContractors.length > 0
        ? props.onlyContractors
        : null;
    const subContractorsFilter =
      props.onlySubContractors !== null && props.onlySubContractors.length > 0
        ? props.onlySubContractors
        : null;
    if (exportPerContractor) {
      const contractors =
        props.onlyContractors ??
        Array.from(
          new Set(
            props.allParticipants
              .filter((p) => props.onlyExams.includes(p.examId))
              .map((p) => p.contractor),
          ),
        );
      const blob = await exportSelectionAdminDashboardPdfPerContractor(
        props.onlyExams,
        Array.from(new Set(groupedContractors)),
        contractors,
        subContractorsFilter,
        props.onlyParticipants,
      );
      const file = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = file;
      a.download = getDashboardDataFileName(exams, "zip");
      a.click();
      URL.revokeObjectURL(file);
    } else {
      const blob = await exportSelectionAdminDashboardPdf(
        props.onlyExams,
        Array.from(new Set(groupedContractors)),
        contractorsFilter,
        subContractorsFilter,
        props.onlyParticipants,
      );
      const file = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = file;
      a.download = getDashboardDataFileName(exams);
      a.click();
      URL.revokeObjectURL(file);
    }
  };

  // shows all contractors on exam(s), no matter if they are selected beforehand or not.
  const matchingParticipants: ExamParticipant[] = [];
  exams.forEach((exam) => {
    const newAddition = props.allParticipants.filter((ap) => ap.examId === exam.id);
    matchingParticipants.push(...newAddition);
  });
  const contractors = Array.from(new Set(matchingParticipants.map((c) => c.contractor)));

  return (
    <div>
      <div className={"flex mb-3"}>
        <div className={"flex-1 border border-slate-200 rounded-sm px-3 py-2"}>
          <table className={"w-full"}>
            <tbody>
              <tr>
                <th className={"text-left font-medium"}>Test days</th>
                <td className={"text-right"}>{examDays.length}</td>
              </tr>
              <tr>
                <th className={"text-left font-medium"}>Tests performed</th>
                <td className={"text-right"}>{props.onlyExams.length}</td>
              </tr>
              {contractors !== null && (
                <tr>
                  <th className={"text-left font-medium"}>Contractors</th>
                  <td className={"text-right"}>{contractors.length}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        <div
          className={"border border-slate-200 rounded-sm w-24 ml-3 bg-slate-100 overflow-hidden"}
        >
          {props.company.logo ? (
            <img
              src={props.company.logo}
              alt={props.company.name}
              className={"h-full w-full object-contain"}
            />
          ) : (
            <></>
          )}
        </div>
      </div>
      <Toggle
        label={"Exporteren per Contractor"}
        value={exportPerContractor}
        onChange={setExportPerContractor}
      />
      {contractors && (
        <div className={"border border-slate-200 rounded-sm px-3 py-2 mt-3"}>
          <h2 className={"font-medium mb-1"}>Groepeer contractors</h2>
          <div className={"text-sm text-slate-600"}>
            Veel kleinere contractors? Groepeer de kleine onder de naam <strong>"Other"</strong>.
            Selecteer contractors om te groeperen.
          </div>
          <div className={"grid grid-cols-1 lg:grid-cols-2 2xl:grid-cols-3 gap-2 mt-2"}>
            {contractors.map((contractor, i) => (
              <label
                key={i}
                className={`flex items-center h-10 px-2 border rounded-sm cursor-pointer text-sm leading-tight ${groupedContractors.includes(contractor) ? "bg-brand-50 border-brand-500" : "border-slate-200"}`}
              >
                <input
                  type={"checkbox"}
                  className={"mr-2"}
                  checked={groupedContractors.includes(contractor)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setGroupedContractors([...groupedContractors, contractor]);
                    } else {
                      setGroupedContractors(groupedContractors.filter((c) => c !== contractor));
                    }
                  }}
                />
                <span>{contractor}</span>
              </label>
            ))}
          </div>
        </div>
      )}

      <ModalFooter text={"Exporteren"} onSubmit={exportAndDownload} />
    </div>
  );
};
